import React, { useState, useEffect } from 'react';
import { ShieldCheck, Info, CheckSquare, Square, Plus, X, Sparkles, Check, Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

export default function FinanceLegalitasSection({ 
  legalitasList = [], 
  onLegalitasChange, 
  masterFinanceLegalitas = [],
  availableVariants = []
}) {
  const [customVariantInputs, setCustomVariantInputs] = useState({});
  const [customVariantsMap, setCustomVariantsMap] = useState({});
  const [expandedVariants, setExpandedVariants] = useState({});

  // Daftar legalitas dasar (hanya sebagai fallback jika database kosong)
  const defaultLegalitasNames = ['BPOM (Per Varian)', 'HAKI (Hak Kekayaan Intelektual)', 'Sertifikasi Halal'];
  
  // Gunakan data dari database jika tersedia, jika tidak gunakan fallback
  const masterLegalitas = masterFinanceLegalitas.length > 0 
    ? masterFinanceLegalitas.map(item => ({
        nama: item.nama,
        harga_beli: item.harga_beli || 0,
        harga_jual_default: item.harga_jual || 0,
        isAvailable: true
      }))
    : defaultLegalitasNames.map(nama => {
        const fallbackHargaJual = nama === 'BPOM (Per Varian)' ? 2500000 : nama === 'Sertifikasi Halal' ? 2000000 : 3000000;
        const fallbackHargaBeli = nama === 'BPOM (Per Varian)' ? 500000 : nama === 'Sertifikasi Halal' ? 500000 : 2000000;
        return {
          nama: nama,
          harga_beli: fallbackHargaBeli,
          harga_jual_default: fallbackHargaJual,
          isAvailable: true
        };
      });

  const isVariantBased = (nama = '') => {
    const upper = nama.toUpperCase();
    return upper.includes('BPOM') || upper.includes('HALAL');
  };

  // Handle checkbox toggle
  const handleToggle = (item) => {
    if (!item.isAvailable) return;
    
    const exists = legalitasList.find(l => l.nama === item.nama);
    if (exists) {
      onLegalitasChange(legalitasList.filter(l => l.nama !== item.nama));
    } else {
      const isVar = isVariantBased(item.nama);
      const initialQty = isVar && availableVariants.length > 0 ? availableVariants.length : 1;
      const unitPrice = item.harga_jual_default || 0;

      onLegalitasChange([...legalitasList, {
        nama: item.nama,
        harga_beli: item.harga_beli,
        harga_jual: unitPrice, // Satuan per varian
        qty_variant: initialQty,
        selected_variants: []
      }]);
    }
  };

  const updateHargaSatuan = (itemNama, unitVal) => {
    const newUnitPrice = unitVal === '' ? '' : (parseFloat(unitVal) || 0);
    onLegalitasChange(legalitasList.map(l => l.nama === itemNama ? { ...l, harga_jual: newUnitPrice } : l));
  };

  const updateQty = (itemNama, qtyVal) => {
    const parsedQty = qtyVal === '' ? '' : Math.max(1, parseInt(qtyVal) || 1);
    onLegalitasChange(legalitasList.map(l => l.nama === itemNama ? { ...l, qty_variant: parsedQty } : l));
  };

  const toggleExpandVariants = (itemNama) => {
    setExpandedVariants(prev => ({ ...prev, [itemNama]: !prev[itemNama] }));
  };

  // Toggle specific variant selection
  const handleVariantToggle = (itemNama, variantName, defaultUnitPrice, combinedVariants = []) => {
    const currentItem = legalitasList.find(l => l.nama === itemNama);
    if (!currentItem) return;

    const currentSelected = Array.isArray(currentItem.selected_variants) ? currentItem.selected_variants : [];
    let updatedSelected = [];

    if (currentSelected.includes(variantName)) {
      updatedSelected = currentSelected.filter(v => v !== variantName);
    } else {
      updatedSelected = [...currentSelected, variantName];
    }

    const newQty = updatedSelected.length > 0 ? updatedSelected.length : (currentItem.qty_variant || 1);
    const unitPrice = currentItem.harga_jual !== undefined && currentItem.harga_jual !== '' ? currentItem.harga_jual : defaultUnitPrice;

    onLegalitasChange(legalitasList.map(l => {
      if (l.nama !== itemNama) return l;
      return {
        ...l,
        selected_variants: updatedSelected,
        qty_variant: newQty,
        harga_jual: unitPrice
      };
    }));
  };

  // Select all variants
  const handleSelectAllVariants = (itemNama, allVariants, defaultUnitPrice) => {
    const currentItem = legalitasList.find(l => l.nama === itemNama);
    if (!currentItem) return;

    const newQty = allVariants.length > 0 ? allVariants.length : 1;
    const unitPrice = currentItem.harga_jual !== undefined && currentItem.harga_jual !== '' ? currentItem.harga_jual : defaultUnitPrice;

    onLegalitasChange(legalitasList.map(l => {
      if (l.nama !== itemNama) return l;
      return {
        ...l,
        selected_variants: [...allVariants],
        qty_variant: newQty,
        harga_jual: unitPrice
      };
    }));
  };

  // Clear / deselect all variants (use numeric qty only)
  const handleClearVariants = (itemNama, defaultUnitPrice) => {
    const currentItem = legalitasList.find(l => l.nama === itemNama);
    if (!currentItem) return;

    const unitPrice = currentItem.harga_jual !== undefined && currentItem.harga_jual !== '' ? currentItem.harga_jual : defaultUnitPrice;

    onLegalitasChange(legalitasList.map(l => {
      if (l.nama !== itemNama) return l;
      return {
        ...l,
        selected_variants: [],
        qty_variant: l.qty_variant || 1,
        harga_jual: unitPrice
      };
    }));
  };

  // Add custom manual variant name
  const handleAddCustomVariant = (itemNama, defaultUnitPrice) => {
    const inputVal = (customVariantInputs[itemNama] || '').trim();
    if (!inputVal) return;

    const existingCustom = customVariantsMap[itemNama] || [];
    if (!existingCustom.includes(inputVal)) {
      setCustomVariantsMap(prev => ({
        ...prev,
        [itemNama]: [...existingCustom, inputVal]
      }));
    }

    const currentItem = legalitasList.find(l => l.nama === itemNama);
    if (currentItem) {
      const currentSelected = Array.isArray(currentItem.selected_variants) ? currentItem.selected_variants : [];
      if (!currentSelected.includes(inputVal)) {
        const updatedSelected = [...currentSelected, inputVal];
        const newQty = updatedSelected.length;
        const unitPrice = currentItem.harga_jual !== undefined && currentItem.harga_jual !== '' ? currentItem.harga_jual : defaultUnitPrice;

        onLegalitasChange(legalitasList.map(l => {
          if (l.nama !== itemNama) return l;
          return {
            ...l,
            selected_variants: updatedSelected,
            qty_variant: newQty,
            harga_jual: unitPrice
          };
        }));
      }
    }

    setCustomVariantInputs(prev => ({ ...prev, [itemNama]: '' }));
  };

  const handleRemoveCustomVariant = (itemNama, variantName, defaultUnitPrice, remainingVariants = []) => {
    setCustomVariantsMap(prev => ({
      ...prev,
      [itemNama]: (prev[itemNama] || []).filter(v => v !== variantName)
    }));

    const currentItem = legalitasList.find(l => l.nama === itemNama);
    if (currentItem) {
      const currentSelected = Array.isArray(currentItem.selected_variants) ? currentItem.selected_variants : [];
      let updatedSelected = currentSelected.filter(v => v !== variantName);
      
      // Pastikan tetap ada minimal 1 jika ada varian lain
      if (updatedSelected.length === 0 && remainingVariants.length > 0) {
        updatedSelected = [remainingVariants[0]];
      }

      const newQty = updatedSelected.length > 0 ? updatedSelected.length : 1;
      const unitPrice = currentItem.harga_jual !== undefined && currentItem.harga_jual !== '' ? currentItem.harga_jual : defaultUnitPrice;

      onLegalitasChange(legalitasList.map(l => {
        if (l.nama !== itemNama) return l;
        return {
          ...l,
          selected_variants: updatedSelected,
          qty_variant: newQty,
          harga_jual: unitPrice
        };
      }));
    }
  };

  return (
    <div className="bg-white border border-[#E6D5B8] rounded-2xl p-6 shadow-sm mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 pb-3 border-b border-[#E6D5B8]/40">
        <h3 className="text-lg font-bold text-[#302621] flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#8C6208]" />
          Legalitas & Sertifikasi (BPOM, HAKI, Halal)
        </h3>
        {availableVariants.length > 0 && (
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#F5EBE1] text-[#8C6208] border border-[#E6D5B8]">
            {availableVariants.length} Varian Terdeteksi di Form
          </span>
        )}
      </div>

      <p className="text-sm text-[#7A695B] mb-5">
        Pilih legalitas yang ingin ditambahkan ke tagihan / penawaran client. <strong>Modal/Harga Beli</strong> terkunci dari database (untuk HPP), sedangkan <strong>Harga Jual</strong> dan <strong>Pilihan Varian</strong> dapat disesuaikan otomatis maupun manual.
      </p>

      <div className="space-y-4">
        {masterLegalitas.map((item, idx) => {
          const isSelected = legalitasList.some(l => l.nama === item.nama);
          const currentItem = legalitasList.find(l => l.nama === item.nama);
          const isVar = isVariantBased(item.nama);

          // Gabungkan list varian dari invoice + varian custom yang ditambahkan
          const customVars = customVariantsMap[item.nama] || [];
          const combinedVariants = Array.from(new Set([...availableVariants, ...customVars]));
          const selectedVariants = currentItem?.selected_variants || [];
          const allSelected = combinedVariants.length > 0 && combinedVariants.every(v => selectedVariants.includes(v));

          const qty = currentItem?.qty_variant || (selectedVariants.length > 0 ? selectedVariants.length : 1);
          const unitPrice = currentItem?.harga_jual !== undefined && currentItem?.harga_jual !== '' ? currentItem.harga_jual : item.harga_jual_default;
          const totalPrice = unitPrice * qty;

          return (
            <div 
              key={idx} 
              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                isSelected 
                  ? 'border-[#D4AF37] bg-gradient-to-b from-[#FAF6EE] to-[#FFFDF9] shadow-sm' 
                  : 'border-[#E6D5B8]/60 bg-white hover:border-[#D4AF37]/50'
              } ${!item.isAvailable ? 'opacity-50 grayscale' : ''}`}
            >
              {/* Main Card Header */}
              <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start sm:items-center gap-3 cursor-pointer select-none" onClick={() => handleToggle(item)}>
                  <input 
                    type="checkbox" 
                    className="w-5 h-5 rounded border-[#E6D5B8] text-[#8C6208] focus:ring-[#D4AF37] mt-0.5 sm:mt-0 cursor-pointer"
                    checked={isSelected}
                    onChange={() => {}} // Handled by container onClick
                    disabled={!item.isAvailable}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-bold ${isSelected ? 'text-[#302621]' : 'text-[#4A3B32]'}`}>{item.nama}</p>
                      {isVar && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                          Multi-Varian
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#7A695B] mt-0.5">
                      {item.isAvailable 
                        ? `Modal HPP: Rp ${fmt(item.harga_beli)} / varian • Tarif Default: Rp ${fmt(item.harga_jual_default)} / varian` 
                        : 'Belum Tersedia Saat Ini'}
                    </p>
                  </div>
                </div>
                
                {isSelected && item.isAvailable && (
                  <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2.5 pt-2 sm:pt-0 border-t sm:border-t-0 border-[#E6D5B8]/40">
                    {/* Qty Input */}
                    <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-xl border border-[#D4AF37]/60 shadow-2xs">
                      <span className="text-xs text-[#8C6208] font-bold">Qty:</span>
                      <input 
                        type="number" 
                        min="1" 
                        value={qty === '' ? '' : (qty !== undefined ? qty : 1)} 
                        onChange={(e) => updateQty(item.nama, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-12 text-sm font-extrabold border-none bg-transparent p-0 focus:ring-0 text-[#302621] text-center"
                      />
                      <span className="text-[11px] text-[#7A695B] font-medium">{isVar ? 'varian' : 'unit'}</span>
                    </div>

                    {/* Unit Price Input */}
                    <div className="flex items-center gap-1.5 bg-white px-2.5 py-1.5 rounded-xl border border-[#D4AF37]/60 shadow-2xs">
                      <span className="text-xs text-[#8C6208] font-bold whitespace-nowrap">Satuan:</span>
                      <span className="text-xs text-[#302621] font-semibold">Rp</span>
                      <input 
                        type="number" 
                        min="0" 
                        value={unitPrice === '' ? '' : (unitPrice !== undefined ? unitPrice : '')} 
                        onChange={(e) => updateHargaSatuan(item.nama, e.target.value)}
                        onWheel={(e) => e.target.blur()}
                        className="w-24 sm:w-28 text-sm font-extrabold border-none bg-transparent p-0 focus:ring-0 text-[#302621] text-right"
                        placeholder="0"
                      />
                    </div>

                    {/* Total calculated badge */}
                    <div className="bg-[#FAF6EE] px-3 py-1.5 rounded-xl border border-[#D4AF37]/40 text-right min-w-[110px]">
                      <span className="text-[10px] text-[#8C6208] font-semibold block leading-none">Total:</span>
                      <span className="text-xs font-black text-[#302621]">Rp {fmt(totalPrice)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Optional Variant Selector Drawer for BPOM & Halal */}
              {isSelected && item.isAvailable && isVar && (
                <div className="border-t border-[#E6D5B8]/50 bg-white/60">
                  <div className="px-4 py-2.5 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => toggleExpandVariants(item.nama)}
                      className="text-xs font-bold text-[#8C6208] hover:text-[#5A3F05] flex items-center gap-1.5 transition-colors cursor-pointer select-none"
                    >
                      <Layers className="w-3.5 h-3.5 text-[#8C6208]" />
                      <span>Rincian Nama Varian {selectedVariants.length > 0 ? `(${selectedVariants.length} Terpilih)` : '(Opsional)'}</span>
                      {expandedVariants[item.nama] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    </button>

                    {expandedVariants[item.nama] && (
                      <div className="flex items-center gap-2">
                        {selectedVariants.length > 0 && (
                          <button
                            type="button"
                            onClick={() => handleClearVariants(item.nama, item.harga_jual_default)}
                            className="text-xs px-2.5 py-1 rounded-lg font-bold flex items-center gap-1 transition-all bg-[#EFE9DB] text-[#4A3B32] hover:bg-[#E3D9C3] border border-[#D5C7A3] shadow-2xs"
                          >
                            <Square className="w-3.5 h-3.5" />
                            Kosongkan Pilihan
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSelectAllVariants(item.nama, combinedVariants, item.harga_jual_default)}
                          className="text-xs px-2.5 py-1 rounded-lg font-bold flex items-center gap-1.5 transition-all bg-[#8C6208] text-white hover:bg-[#6F4E06] shadow-xs"
                        >
                          <CheckSquare className="w-3.5 h-3.5" />
                          Pilih Semua ({combinedVariants.length})
                        </button>
                      </div>
                    )}
                  </div>

                  {expandedVariants[item.nama] && (
                    <div className="px-4 pb-4 pt-1 space-y-3 border-t border-[#E6D5B8]/30">
                      {/* Variant Pills Grid */}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {combinedVariants.length === 0 ? (
                          <div className="w-full text-center py-3 bg-[#FAF7F2] rounded-xl border border-dashed border-[#E6D5B8]">
                            <p className="text-xs text-[#8C7A6B]">
                              Belum ada varian di form. Anda dapat mengetik nama varian baru di bawah ini.
                            </p>
                          </div>
                        ) : (
                          combinedVariants.map((vName, vIdx) => {
                            const isVarSelected = selectedVariants.includes(vName);
                            const isCustom = customVars.includes(vName);

                            return (
                              <div
                                key={vIdx}
                                onClick={() => handleVariantToggle(item.nama, vName, item.harga_jual_default, combinedVariants)}
                                className={`group flex items-center gap-2 px-3.5 py-1.5 rounded-xl border text-xs font-bold cursor-pointer transition-all duration-150 select-none ${
                                  isVarSelected
                                    ? 'bg-gradient-to-r from-[#F7EFE1] to-[#FFF8EC] border-[#D4AF37] text-[#5A3F05] shadow-xs ring-1 ring-[#D4AF37]/30'
                                    : 'bg-white border-[#E6D5B8]/80 text-[#7A695B] hover:border-[#D4AF37]/60 hover:bg-[#FAF6EE]'
                                }`}
                              >
                                <div className={`w-4 h-4 rounded-md flex items-center justify-center transition-colors ${
                                  isVarSelected ? 'bg-[#8C6208] text-white' : 'border border-[#C8B89E] group-hover:border-[#8C6208]'
                                }`}>
                                  {isVarSelected && <Check className="w-3 h-3 stroke-[3]" />}
                                </div>

                                <span>{vName}</span>

                                {isCustom && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveCustomVariant(item.nama, vName, item.harga_jual_default, combinedVariants.filter(v => v !== vName));
                                    }}
                                    className="ml-1 p-0.5 rounded-full hover:bg-red-100 hover:text-red-600 text-gray-400 transition-colors"
                                    title="Hapus varian manual ini"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Add Manual/Custom Variant Input */}
                      <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type="text"
                            value={customVariantInputs[item.nama] || ''}
                            onChange={(e) => setCustomVariantInputs({ ...customVariantInputs, [item.nama]: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCustomVariant(item.nama, item.harga_jual_default);
                              }
                            }}
                            placeholder="Ketik nama varian baru / manual (misal: Vanilla Mist)..."
                            className="w-full text-xs bg-white border border-[#E6D5B8] rounded-xl px-3 py-2 text-[#302621] placeholder:text-[#A8988B] focus:outline-hidden focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                          />
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleAddCustomVariant(item.nama, item.harga_jual_default)}
                          className="bg-[#302621] hover:bg-[#4A3B32] text-white text-xs font-semibold px-4 h-8 rounded-xl shrink-0 gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Tambah Varian
                        </Button>
                      </div>

                      {/* Summary Footer for this certification */}
                      <div className="flex flex-wrap items-center justify-between text-xs text-[#8C6208] bg-[#FAF6EE] px-3.5 py-2 rounded-xl border border-[#E6D5B8]/70 mt-2">
                        {selectedVariants.length > 0 ? (
                          <>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                              <span className="font-semibold text-[#5A3F05]">Varian Terdaftar di Invoice:</span>
                              <span className="text-[#7A695B] italic">{selectedVariants.join(', ')}</span>
                            </div>
                            <div className="font-bold text-[#5A3F05] shrink-0">
                              {selectedVariants.length} Varian × Rp {fmt(unitPrice)} = Rp {fmt(selectedVariants.length * unitPrice)}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between w-full text-xs text-[#7A695B]">
                            <span className="italic">💡 Mode Qty Angka Langsung (Rincian nama varian tidak akan dicetak di invoice)</span>
                            <span className="font-bold text-[#5A3F05]">{qty} Varian × Rp {fmt(unitPrice)} = Rp {fmt(qty * unitPrice)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
